// NOTE: uses commentsColorMap var saved to store from the Study component
import React from "react"
import { useShallow } from "zustand/react/shallow"
import { Tooltip, Flex } from "antd"
import dayjs from "dayjs"
import parse from "html-react-parser"
import { Badge } from "components-design-system"
import { SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring.js"
import scrollToElement from "utilities/scrollToElement.js"
import { orphanCommentKeys } from "../../../RJS/widgets/utils"

// store
import useAuthoringCommentsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringCommentsStore"
import useAuthoringCommentActions from "containers/studies/Study/subcomponents/Authoring/hooks/actions/useAuthoringCommentActions"

// styles
import "./CommentItem.scss"

const CommentItem = ({ comment, isReviewerView }) => {
  const { tags, field_key, field_name, author_fullname, action_date, text, resolved, favorite } = comment
  const { openComment, setOpenComment, commentsColorMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      setOpenComment: state.setOpenComment,
      commentsColorMap: state.commentsColorMap,
    }))
  )

  const [isFavorite, setIsFavorite] = React.useState(favorite)
  const [isResolved, setIsResolved] = React.useState(resolved)
  const { handleFavorite, handleResolved } = useAuthoringCommentActions()

  // When (un)favorite from the corresponding RJCommentItem, reflect change on this comment item
  React.useEffect(() => {
    setIsFavorite(comment?.favorite)
  }, [comment?.favorite])

  let name = ""
  let initials = ""

  if (author_fullname) {
    let names = author_fullname.split(" ")
    if (names[0] && names[0].length > 0) {
      name += names[0]
      initials += names[0][0]
    }
    if (names[1] && names[1].length > 0) {
      name += ` ${names[1][0]}.`
      initials += names[1][0]
    }
    name.toUpperCase()
    initials.toUpperCase()
  }

  const classNames = React.useMemo(() => {
    const ret = "comment-container"
    if (openComment === field_key) return "comment-container active-comment"

    return ret
  }, [openComment])

  const onOpenComment = () => {
    setOpenComment(field_key)
    scrollToField(field_key)
  }

  const scrollToField = (jpath) => {
    const scrollableElement = isReviewerView
      ? document.getElementById(SCROLLABLE_AUTHORING_CONTAINER.REVIEW_ID)
      : document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]

    const targetElement = document.getElementById(jpath)

    if (targetElement && scrollableElement) {
      scrollToElement({
        scrollableElement,
        targetElement,
        scrollType: "instant",
      })
    } else if (!targetElement && scrollableElement) {
      let code = jpath.slice(0, 2)
      if (orphanCommentKeys?.[code]) {
        const targetElement = document.getElementById(orphanCommentKeys[code])
        scrollToElement({
          scrollableElement,
          targetElement,
          scrollType: "instant",
        })
      } else if (jpath.includes("ParticipantFlowGroup")) {
        const targetElement = document.getElementById("root_results_participant_flow")
        scrollToElement({
          scrollableElement,
          targetElement,
          scrollType: "instant",
        })
      } else if (jpath.includes("BaselineReportingGroup")) {
        const targetElement = document.getElementById("root_results_baseline_characteristics")
        scrollToElement({
          scrollableElement,
          targetElement,
          scrollType: "instant",
        })
      } else if (jpath.includes("OutcomeRptGroup")) {
        const targetElement = document.getElementById("root_results_outcome_measures")
        scrollToElement({
          scrollableElement,
          targetElement,
          scrollType: "instant",
        })
      } else if (jpath.includes("ReportedEvents")) {
        const targetElement = document.getElementById("root_results_adverse_events")
        scrollToElement({
          scrollableElement,
          targetElement,
          scrollType: "instant",
        })
      }
    }
  }

  const onResolveSuccess = () => {
    setIsResolved(!isResolved)
  }

  const onFavoriteSuccess = () => {
    setIsFavorite(!isFavorite)
  }

  return (
    <div className={"authoring-comment"}>
      <div
        className="comment-initials"
        onClick={onOpenComment}
      >
        {initials}
      </div>
      <div
        id={field_key + "-side-panel-comment"}
        className={classNames}
      >
        <Flex
          align="center"
          justify="space-between"
          style={{ width: "100%" }}
        >
          <div
            className="flex items-center cursor-pointer"
            onClick={onOpenComment}
          >
            <div className="author-header">{name}</div>
            <div className="date-text">{dayjs(action_date).format("MMM DD, YYYY - h:mA")}</div>
          </div>
          <Flex
            gap={6}
            align="center"
          >
            <Tooltip title={favorite ? "Unfavorite" : "Favorite"}>
              <i
                className={`fal fa-heart favorite-icon${isFavorite ? " favorite" : ""}`}
                onClick={() => handleFavorite(comment, onFavoriteSuccess)}
              />
            </Tooltip>
            <Tooltip title={resolved ? "Unresolve" : "Resolve"}>
              <i
                className={`fal fa-check-circle resolved-icon${isResolved ? " resolved" : ""}`}
                onClick={() => handleResolved(comment, onResolveSuccess)}
              />
            </Tooltip>
          </Flex>
        </Flex>
        <div
          className="flex items-center cursor-pointer"
          onClick={onOpenComment}
        >
          <div className="field-name">{field_name.toUpperCase()}</div>
        </div>
        <div
          className="cursor-pointer"
          onClick={onOpenComment}
        >
          <div className="comment-text html-parse-container">{parse(text)}</div>
        </div>
        <div
          className="flex cursor-pointer"
          onClick={onOpenComment}
          style={{ marginTop: 8 }}
        >
          <Badge
            value={tags?.[0] && tags[0]}
            colorName={commentsColorMap?.[tags?.[0]]}
          />
        </div>
      </div>
    </div>
  )
}

export default CommentItem
